import { CategoryApi } from "@/api/CategoryApi";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { ProductFormData } from "@/formTypes/ProductFormDataType";
import { ReportDataProps, ReportTitleProps } from "@components/dynamic/ReportPage";
import { formatCant } from "@utils/Cant";

/** Valor del select "Todas las categorias" en el formulario del reporte. */
export const ALL_CATEGORIES_OPTION = "-2";

export const ALL_CATEGORIES_LABEL = "Todas las categorias";

/**
 * La hoja de impresion es vertical (no hay `@page landscape` en la variacion de
 * impresion), por lo que el PDF solo admite 8 columnas legibles. El Excel no
 * tiene ese limite.
 */
export const PDF_MAX_PROPERTIES = 8;

/** Prefijo de los checkbox de propiedades dentro del formulario dinamico. */
export const PROPERTY_FIELD_PREFIX = "prop_";

export type ProductReportPropertyKey = keyof ProductFormData;

export type ProductReportProperty = {
    key: ProductReportPropertyKey,
    label: string,
    isNumeric?: boolean,
    /** Peso relativo del ancho de la columna en el PDF. */
    widthWeight?: number,
    format?: (product: ProductDataType) => string
}

const formatText = (value: unknown): string => {
    return value == null ? "" : `${value}`;
}

/**
 * Los montos se emiten sin separador de miles a proposito: `ReportPage` convierte
 * las columnas numericas con `Number(...)` al exportar a Excel, y el formato es-CR
 * de `formatMoneyValue` (con puntos de miles) no es convertible.
 */
export const formatReportAmount = (value: unknown): string => {
    return Number(value || 0).toFixed(2);
}

const formatBoolean = (value: unknown): string => {
    return value ? "Si" : "No";
}

export const PRODUCT_REPORT_PROPERTIES: ProductReportProperty[] = [
    { key: "codigo", label: "Codigo", widthWeight: 2 },
    { key: "descripcion_fact", label: "Descripcion Factura", widthWeight: 4 },
    { key: "descripcion", label: "Descripcion", widthWeight: 4 },
    { key: "codigo_cabys", label: "Codigo Cabys", widthWeight: 2 },
    { key: "codigobarras", label: "Codigo de Barras", widthWeight: 2 },
    { key: "unidad_medida", label: "Unidad de Medida", widthWeight: 2 },
    { key: "existencias", label: "Existencias", isNumeric: true, widthWeight: 2, format: (p) => formatCant(p.existencias) },
    { key: "minimo", label: "Minimo", isNumeric: true, widthWeight: 2, format: (p) => formatCant(p.minimo) },
    { key: "maximo", label: "Maximo", isNumeric: true, widthWeight: 2, format: (p) => formatCant(p.maximo) },
    { key: "costo", label: "Costo", isNumeric: true, widthWeight: 2, format: (p) => formatReportAmount(p.costo) },
    { key: "precio1", label: "Precio 1", isNumeric: true, widthWeight: 2, format: (p) => formatReportAmount(p.precio1) },
    { key: "precio2", label: "Precio 2", isNumeric: true, widthWeight: 2, format: (p) => formatReportAmount(p.precio2) },
    { key: "precio3", label: "Precio 3", isNumeric: true, widthWeight: 2, format: (p) => formatReportAmount(p.precio3) },
    { key: "precio4", label: "Precio 4", isNumeric: true, widthWeight: 2, format: (p) => formatReportAmount(p.precio4) },
    { key: "comision", label: "Comision", isNumeric: true, widthWeight: 2, format: (p) => formatReportAmount(p.comision) },
    { key: "tipo_impuesto", label: "Tipo Impuesto", widthWeight: 2 },
    { key: "porcentaje_impuesto", label: "% Impuesto", widthWeight: 2 },
    { key: "exento", label: "Exento", widthWeight: 1, format: (p) => formatBoolean(p.exento) },
    { key: "activo", label: "Activo", widthWeight: 1, format: (p) => formatBoolean(p.activo) },
    { key: "existencias_compartidas", label: "Existencias Compartidas", widthWeight: 2, format: (p) => formatBoolean(p.existencias_compartidas) },
    { key: "partidaArancelaria", label: "Partida Arancelaria", widthWeight: 2 },
    { key: "tipo_producto", label: "Tipo Producto", widthWeight: 2 },
    { key: "visibilidad", label: "Visibilidad", widthWeight: 2 },
];

const PROPERTIES_BY_KEY: Record<string, ProductReportProperty> = PRODUCT_REPORT_PROPERTIES.reduce((a, p) => {
    return { ...a, [`${p.key}`]: p };
}, {} as Record<string, ProductReportProperty>);

/** Serializa las propiedades para el segmento de URL del reporte. */
export const encodeReportProperties = (keys: ProductReportPropertyKey[]): string => {
    return keys.filter(k => !!PROPERTIES_BY_KEY[`${k}`]).join(",");
}

/**
 * Lee el segmento de URL descartando claves desconocidas y respetando el orden
 * del catalogo, de modo que las columnas sean siempre deterministas.
 */
export const decodeReportProperties = (raw: string): ProductReportPropertyKey[] => {
    const requested = `${raw || ""}`.split(",").map(k => k.trim()).filter(k => !!k);
    return PRODUCT_REPORT_PROPERTIES
        .filter(p => requested.includes(`${p.key}`))
        .map(p => p.key);
}

export type ProductsByCategoryParams = {
    categoryId: string,
    properties: ProductReportPropertyKey[],
    includeInactive?: boolean
}

export type ProductCategoryGroup = {
    categoryId: string,
    title: string,
    products: ProductDataType[]
}

/** Campos que el reporte siempre necesita, aunque el usuario no los muestre. */
const MANDATORY_SELECTION = ["id", "codigo", "descripcion_fact", "temporal", "activo"];

export const buildSelectionSet = (keys: ProductReportPropertyKey[]): string[] => {
    return Array.from(new Set([...MANDATORY_SELECTION, ...keys.map(k => `${k}`)]));
}

export const resolveCategoryLabel = (categoryId: string, categories: Record<string, string>): string => {
    if (!categoryId || categoryId === ALL_CATEGORIES_OPTION) {
        return ALL_CATEGORIES_LABEL;
    }
    return categories[categoryId] || "";
}

/**
 * Devuelve los productos agrupados por categoria. Es la unica fuente de datos del
 * reporte: la usan tanto la version impresa como la exportada a Excel.
 */
export const fetchProductsByCategory = async (params: ProductsByCategoryParams): Promise<ProductCategoryGroup[]> => {
    const { categoryId, properties, includeInactive = false } = params;

    const categoryList = await CategoryApi.optionsList({ activo: { eq: true } });
    const selectionSet = buildSelectionSet(properties);

    const categoryIds = categoryId && categoryId !== ALL_CATEGORIES_OPTION
        ? [categoryId]
        : Object.keys(categoryList);

    const groups: ProductCategoryGroup[] = [];

    for (const id of categoryIds) {
        let nextToken: string | undefined = undefined;
        let products: ProductDataType[] = [];

        do {
            const { items, nextToken: nt } = await ProductApi.fetchAllByCategory(id, {}, nextToken, 1000, selectionSet);
            // El token debe reenviarse: sin esto se vuelve a pedir la primera pagina
            // indefinidamente cuando la categoria supera el limite de la consulta.
            nextToken = nt;
            products = [
                ...products,
                ...items.filter(p => !p.temporal && (includeInactive || p.activo))
            ];
        } while (nextToken);

        products.sort((a, b) => {
            const nameA = a.descripcion_fact || "";
            const nameB = b.descripcion_fact || "";

            return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
        });

        groups.push({
            categoryId: id,
            title: categoryList[id] || "",
            products
        });
    }

    return groups;
}

/**
 * Genera las cabeceras de columna. Las que sobran del limite de PDF se marcan con
 * `hideFromPdf`, con lo que `ReportPage` las omite al imprimir pero las mantiene
 * en el Excel.
 */
export const buildReportTitles = (keys: ProductReportPropertyKey[]): ReportTitleProps[] => {
    const properties = keys.map(k => PROPERTIES_BY_KEY[`${k}`]).filter(p => !!p);
    const pdfProperties = properties.slice(0, PDF_MAX_PROPERTIES);
    const totalWeight = pdfProperties.reduce((a, p) => a + (p.widthWeight || 1), 0) || 1;

    return properties.map((p, index) => {
        const hideFromPdf = index >= PDF_MAX_PROPERTIES;
        const width = hideFromPdf ? "0%" : `${((p.widthWeight || 1) * 100 / totalWeight).toFixed(2)}%`;

        return {
            label: p.label,
            width,
            isNumeric: p.isNumeric,
            ...(hideFromPdf ? { hideFromPdf: true } : {})
        };
    });
}

/**
 * Arma las filas del reporte intercalando una fila de encabezado por categoria.
 * `categoryClassName` lo aporta la pagina de impresion (clase de su modulo SCSS)
 * para no introducir estilos globales.
 */
export const buildReportRows = (
    groups: ProductCategoryGroup[],
    keys: ProductReportPropertyKey[],
    categoryClassName?: string
): ReportDataProps<ProductDataType>[] => {
    const properties = keys.map(k => PROPERTIES_BY_KEY[`${k}`]).filter(p => !!p);

    return groups.flatMap(group => {
        const categoryRow: ReportDataProps<ProductDataType> = {
            excludeFromTotal: true,
            className: categoryClassName,
            values: properties.map((p, index) => index === 0 ? group.title : "")
        };

        const productRows = group.products.map(product => {
            return {
                sourceData: product,
                values: properties.map(p => {
                    return p.format ? p.format(product) : formatText(product[p.key]);
                })
            };
        });

        return [categoryRow, ...productRows];
    });
}
